import {Button} from '@/components/ui/button/Button'
import {TKey, useTranslation} from '@/translations/i18n'
import {routes} from '@/utilities/constants/routes'
import {useNavigate, useRouteError} from 'react-router-dom'
import Logo from '@assets/images/logo.svg'
import {
    StyledErrorBoundaryHeading,
    StyledErrorBoundaryMessage,
    StyledRouterErrorBoundaryContainer
} from '@/route-guards/router-error-boundary/style'
import {ArrowLeftIcon} from '@/components/ui/icon/Icon'
import {useAuthStore} from '@/features/auth/store/store'

export const RouterErrorBoundary = () => {
    const {t} = useTranslation()
    const routeError = useRouteError()
    const navigate = useNavigate()
    const user = useAuthStore(state => state.user)

    // Type guard to check if the error has expected properties
    const errorHasExpectedProperties = (routeError: unknown): routeError is {status: number} => {
        const validStatusCodes = ['400', '401', '403', '404', '500']
        return (
            typeof routeError === 'object' &&
            routeError != null &&
            'status' in routeError &&
            typeof routeError.status == 'number' &&
            validStatusCodes.includes(routeError.status.toString())
        )
    }

    // Get error message from status
    const getErrorMessage = (status: number): string => {
        const mappedCodes: Record<string, TKey> = {
            '400': 'route_errors:bad_request',
            '401': 'route_errors:unauthorized',
            '403': 'route_errors:forbidden',
            '404': 'route_errors:not_found',
            '500': 'route_errors:internal_server_error'
        }

        return t(mappedCodes[status.toString()]) || t('errors:unknown_error')
    }

    // Extract error message safely
    const errorMessage = errorHasExpectedProperties(routeError)
        ? getErrorMessage(routeError.status)
        : t('errors:unknown_error')

    return (
        <StyledRouterErrorBoundaryContainer fullWidth fullHeight>
            <img src={Logo} alt={'logo'} width={48} />
            <StyledErrorBoundaryHeading>{t('errors:default')}</StyledErrorBoundaryHeading>
            <StyledErrorBoundaryMessage>{errorMessage}</StyledErrorBoundaryMessage>
            <Button size="xl" variant="secondary" onClick={() => navigate(routes.DASHBOARD.path, {replace: true})}>
                <ArrowLeftIcon />
                {user ? t('commons:back_to_homepage') : t('commons:back_to_login')}
            </Button>
        </StyledRouterErrorBoundaryContainer>
    )
}
